import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useCashierAuth } from './useCashierAuth';
import { DEVICE_ERRORS, shiftDeviceNameFromError } from '../utils/deviceErrors';

type ErrorResult = { code?: string; message?: string; data?: unknown };

/**
 * For cash drop / closing / handover: when the server refuses the operation because of the device,
 * explain it and leave the page. Returns true when the error was handled.
 * - WRONG_DEVICE → toast "Your shift is running on …", refresh the device, go to `backTo` (the shift page,
 *   which then shows the read-only banner).
 * - DEVICE_UNKNOWN → the session has no device: sign out and go to sign-in.
 */
export function useDeviceErrorRedirect(): (res: ErrorResult, backTo: string) => boolean {
  const navigate = useNavigate();
  const { logout, refreshMe } = useCashierAuth();

  return useCallback(
    (res: ErrorResult, backTo: string) => {
      if (res.code === 'DEVICE_UNKNOWN') {
        toast.error(DEVICE_ERRORS.DEVICE_UNKNOWN.message);
        logout();
        navigate('/login', { replace: true });
        return true;
      }
      if (res.code === 'WRONG_DEVICE') {
        const name = shiftDeviceNameFromError(res.data) || 'another device';
        toast.error(DEVICE_ERRORS.WRONG_DEVICE.message.replace('{device}', name));
        void refreshMe();
        navigate(backTo, { replace: true });
        return true;
      }
      return false;
    },
    [logout, navigate, refreshMe]
  );
}
